<script lang="ts" setup>
	import type { PickerMode } from 'ant-design-vue/lib/vc-picker/interface'

	const isFilterOpen = shallowRef(false)
	const { placeholder = 'Filter', ...props } = defineProps<{
		value: Partial<DateFilterParameter> | null
		filterMonth?: boolean
		filterJustDate?: boolean
		placeholder?: string
	}>()
	const filterOptions = reactive<
		Record<
			string,
			{
				key: string
				isOpen: boolean
				value: string
				picker: PickerMode
				format: string
				hidden?: boolean
			}
		>
	>({
		'start year': {
			key: 'start_year',
			isOpen: false,
			value: '',
			picker: 'year' as PickerMode,
			format: 'YYYY',
			hidden: props.filterJustDate
		},
		'end year': {
			key: 'end_year',
			isOpen: false,
			value: '',
			picker: 'year' as PickerMode,
			format: 'YYYY',
			hidden: props.filterJustDate
		},
		'start month': {
			key: 'start_month',
			isOpen: false,
			value: '',
			picker: 'month' as PickerMode,
			format: 'MMMM',
			hidden: !props.filterMonth || props.filterJustDate
		},
		'end month': {
			key: 'end_month',
			isOpen: false,
			value: '',
			picker: 'month' as PickerMode,
			format: 'MMMM',
			hidden: !props.filterMonth || props.filterJustDate
		},
		'Start Date': {
			key: 'start_date',
			isOpen: false,
			value: '',
			picker: 'date' as PickerMode,
			format: 'DD/MM/YYYY',
			hidden: !props.filterJustDate
		},
		'End Date': {
			key: 'end_date',
			isOpen: false,
			value: '',
			picker: 'date' as PickerMode,
			format: 'DD/MM/YYYY',
			hidden: !props.filterJustDate
		}
	})
	export type DateFilterParameter = {
		start_year?: string
		end_year?: string
		start_month?: string
		end_month?: string
		start_date?: string
		end_date?: string
	}
	const { disableFutureYr } = useCustomDate()

	const emits = defineEmits<{ (event: 'update:value', param: Partial<DateFilterParameter>): void }>()

	const filter = () => {
		const getActiveOptions = Object.keys(filterOptions).filter(key => !filterOptions?.[key]?.hidden)
		const list: Partial<DateFilterParameter> = {}
		getActiveOptions.forEach(key => {
			const getKey = String(filterOptions?.[key]?.key ?? '')
			Object.assign(list, {
				[getKey]: filterOptions?.[key]?.value
			})
		})
		emits('update:value', list)
		isFilterOpen.value = false
	}
</script>

<template>
	<a-dropdown trigger="click" :open="isFilterOpen">
		<div class="flex flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap">
			<button
				type="button"
				aria-label="filter date"
				class="flex items-center gap-x-2.5 rounded border border-gray-300 px-2 py-1.5"
				@click="isFilterOpen = !isFilterOpen"
			>
				<div class="flex items-center gap-x-2">
					<lazy-tools-icon icon="notes" dir="other" :height="16" :width="16" />
					<span class="text-xs font-medium tracking-wide text-gray-700"> {{ placeholder }} </span>
				</div>
				<lazy-tools-icon icon="caret-down" dir="arrows" />
			</button>

			<div class="inline-block max-w-[300px] gap-4 space-x-3 overflow-x-auto">
				<div
					v-if="filterOptions['start month']?.value || filterOptions['start year']?.value"
					class="inline h-[20px] w-full rounded bg-purple-50 p-1.5 px-2 text-center text-[10px] capitalize leading-[20px] tracking-wide text-purple-500"
				>
					{{ filterOptions['start month']?.value }} {{ filterOptions['start year']?.value }}
				</div>
				<div
					v-if="filterOptions['end month']?.value || filterOptions['end year']?.value"
					class="inline h-[20px] w-full rounded bg-purple-50 p-1.5 px-2 text-center text-[10px] capitalize leading-[20px] tracking-wide text-purple-500"
				>
					{{ filterOptions['end month']?.value }} {{ filterOptions['end year']?.value }}
				</div>
			</div>
		</div>

		<template #overlay>
			<div class="light-shadow relative z-10 inline-flex min-h-[220px] flex-col rounded-xl bg-white px-4 py-5">
				<div class="mb-5 flex items-center justify-between border-b pb-4">
					<h3 class="font-semibold tracking-wider text-gray-600"> Filter </h3>
					<button class="relative" type="button" @click="isFilterOpen = false">
						<lazy-tools-icon
							icon="cancel"
							dir="line"
							:height="13"
							:width="13"
							path-class="stroke-gray-600"
						/>
					</button>
				</div>
				<ul class="grid min-w-[320px] grid-cols-2 gap-5">
					<template v-for="(item, k) in filterOptions">
						<li v-if="!item.hidden" :key="k" class="w-full">
							<div class="flex-1 space-y-2 tracking-wider">
								<h4 class="label text-xs font-medium capitalize text-gray-500" role="label">
									{{ k }}
								</h4>

								<a-date-picker
									v-model:value="item.value"
									v-model:open="item.isOpen"
									size="small"
									class="w-full border-gray-700 !py-1.5"
									:format="item?.format"
									input-read-only
									:picker="item.picker"
									:value-format="item.format"
									:disabled-date="disableFutureYr"
								>
									<template #suffixIcon>
										<lazy-tools-icon
											dir="arrows"
											icon="caret-down-r"
											path-class="stroke-gray-700"
										/>
									</template>
								</a-date-picker>
							</div>
						</li>
					</template>
				</ul>

				<div class="flex justify-end">
					<button
						type="button"
						class="mt-8 w-full justify-center rounded-md border border-primary-2 px-10 py-2 font-medium tracking-wider text-primary-2 transition-all hover:bg-primary-2 hover:text-white"
						@click="filter"
					>
						Filter
					</button>
				</div>
			</div>
		</template>
	</a-dropdown>
</template>
